import MaterialTable from 'material-table';
import React, { useEffect, useState } from 'react';

import { ICampaignPrebidInfo } from '~/src/dash/api/campaign/campaign_models';
import { getDebugCampaignPacingData } from '~/src/dash/api/pacing/controller';
import { IDebugCampaignPacingModel } from '~/src/dash/api/pacing/models';

export interface IProps {
  campaignID: string;
  publisherID: string;
  totalGoal: string;
  prebidInfo?: ICampaignPrebidInfo;
}

interface ITableData {
  site_id: string;
  weight: number;
  requests: string;
  filled: string;
  impressions: string;
  requestsAfterRenderRate: string;
  renderRate: string;
  dice_rollpercentage: string;
}

function numberWithCommas(x: number, maxDigits: number = 2): string {
  return x ? x.toFixed(maxDigits).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '0';
}

export const CampaignDebugPacingTable = (props: IProps) => {
  const [pacingData, setPacingData] = useState(null as any as IDebugCampaignPacingModel);
  const [processedData, setProcessedData] = useState(null as any as ITableData[]);
  const [msg, setMsg] = useState('');

  function processCampaignData(data: IDebugCampaignPacingModel) {
    const out = [] as ITableData[];

    if (data.formula_data_by_site_id === null || data.formula_data_by_site_id === undefined) {
      setMsg(
        'No data found, you must have at least 1 campaign with Low Pacing (< 150 req/h) OR Channel Weights enabled + at least 1 request on the last 6 hours',
      );
      return;
    }

    for (const [key, value] of Object.entries(data.formula_data_by_site_id)) {
      let renderRate = 1;
      if (
        !(value.debug_requests_data.Impressions === 0 || value.debug_requests_data.Filled === 0)
      ) {
        renderRate = value.debug_requests_data.Impressions / value.debug_requests_data.Filled;
      }

      out.push({
        requests: numberWithCommas(value.debug_requests_data.Requests),
        filled: numberWithCommas(value.debug_requests_data.Filled),
        impressions: numberWithCommas(value.debug_requests_data.Impressions),
        renderRate: numberWithCommas(renderRate * 100) + '%',
        requestsAfterRenderRate: numberWithCommas(
          value.debug_requests_data.RequestsAfterRenderRate,
        ),
        dice_rollpercentage: value.dice_rollpercentage.toFixed(2) + '%',
        site_id: key,
        weight: value.weight,
      });
    }

    setProcessedData(out);
  }

  const fetchCampaignPacingData = () => {
    getDebugCampaignPacingData(props.publisherID, props.campaignID).then(([data, err]) => {
      if (data !== undefined && data !== null && Object.keys(data).length !== 0) {
        setPacingData(data);
        setMsg('');
        try {
          processCampaignData(data);
        } catch (e) {
          console.error(e);
          setMsg(
            'Error while processing campaign data. Please contact your publica account manager',
          );
        }
      }
      if (err !== undefined) {
        setMsg(
          "Can't fetch pacing data. Your campaign may not be low pacing or we don't have enough history",
        );
        console.error(err);
      }
    });
  };

  useEffect(() => {
    fetchCampaignPacingData();
  }, [props.campaignID, props.publisherID]);

  if (msg !== '') {
    return <p>{msg}</p>;
  }

  if (pacingData == null || processedData == null) {
    return <p>Loading...</p>;
  }

  const renderTable = (): React.ReactElement => {
    if ((processedData || []).length === 0) {
      return (
        <>
          <p>No data found, you must have at least:</p>
          <ul>
            <li>1 campaign with Low Pacing (less than 150 req/h) OR Channel Weights enabled</li>
            <li>1 request on the last 6 hours</li>
          </ul>
        </>
      );
    }
    return (
      <>
        <MaterialTable
          title=''
          columns={[
            { title: 'Channel ID', field: 'site_id' },
            { title: 'Weight', field: 'weight', type: 'numeric', searchable: false },
            { title: 'Avg Requests /h', field: 'requests', searchable: false },
            { title: 'Avg Imps /h', field: 'impressions', searchable: false },
            { title: 'Avg Filled /h', field: 'filled', searchable: false },
            { title: 'Render Rate', field: 'renderRate', searchable: false },
            {
              title: 'Avg Requests after render rate /h',
              field: 'requestsAfterRenderRate',
              searchable: false,
            },
            { title: 'Pacing Rate', field: 'dice_rollpercentage', searchable: false },
          ]}
          options={{
            search: true,
            pageSize: 15,
            pageSizeOptions: [15, 30, 100],
          }}
          data={processedData}
        />
      </>
    );
  };

  return (
    <div>
      <p id={`pb-pacing-data-total-goal`}>
        Total Goal: {numberWithCommas(Number(props.totalGoal))}
      </p>
      <p id={`pb-pacing-data-cumulative-goal`}>
        Cumulative Pacing Goal: {numberWithCommas(pacingData.cumulative_pacing_goal)}
      </p>
      <p id={`pb-pacing-data-total-imps`}>
        Total Impressions:{' '}
        {numberWithCommas(
          pacingData.total_impressions_realtime > pacingData.total_impressions
            ? pacingData.total_impressions_realtime
            : pacingData.total_impressions,
        )}
      </p>
      <p id={`pb-pacing-data-hour-goal`}>
        Goal for this hour: {numberWithCommas(pacingData.goal_for_this_hour)}
      </p>
      <p id={`pb-pacing-data-sum-weights`}>
        Sum of Weights: {numberWithCommas(pacingData.sum_of_weights)}
      </p>
      <p id={`pb-pacing-data-hour-number`}>
        Number of hours: {numberWithCommas(pacingData.NumberOfHours)}
      </p>
      <p id={`pb-pacing-data-hour-number-afterdayparting`}>
        Number of hours (after dayparting):{' '}
        {numberWithCommas(pacingData.NumberOfHourAfterDayparting)}
      </p>
      <p id={`pb-pacing-data-current-hour-number`}>
        Current number of hours (after dayparting):{' '}
        {numberWithCommas(pacingData.NumberOfHoursDoneAfterDayparting)}
      </p>
      <p id={`pb-pacing-data-low-pacing`}>
        Low Pacing: {(props.prebidInfo || { low_pacing: false }).low_pacing ? 'true' : 'false'}
      </p>
      {renderTable()}
      <h3>Update of "end date" field:</h3>
      <p id={`pb-pacing-data-hour-last-update-time`}>
        Time of last update: {pacingData.PacingUpdateTime}
      </p>
      <p id={`pb-pacing-data-hour-pacing-update-nbr-imps`}>
        Nbr of impression(s) at update: {numberWithCommas(pacingData.PacingUpdateNbrImps)}
      </p>
    </div>
  );
};
